import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAssessmentInfo, putAssessmentInfo } from "../../../redux/actions/assessmentInfo/assessmentInfo";
import { selectELSLookups, selectGetAggregateSiteAssessmentInfo, selectMenu, selectProfileGetOverview, selectSaveData, selectSaveResult } from "../../../redux/selectors/exportSelectorPaths";
import { ProfileOverviewState } from "../../../redux/reducers/getProfile/getProfileOverviewReducer";
import { SaveDataState } from "../../../redux/reducers/getSaveData/saveDataReducer";
import { SaveResultState } from "../../../redux/reducers/getSaveData/saveResultReducer";
import { AggregateSiteAssessmentInfoState } from "../../../types/assessment_info/AggregateSiteAssessmentInfoState";
import { AggregateSiteAssessmentInfo, defaultAggregateSiteAnalysisTypes, defaultAggregateSiteAssessmentInfo } from "../../../types/assessment_info/aggregateSites/AggregateSiteData";
import { GetElsLookupsState } from "../../../redux/reducers/getLookups/getElsLookupsReducer";
import { getElsLookups } from "../../../redux/actions/lookups/lookups";
import Input from "../../common/Input/Input";
import Asterisk from "../../common/Asterisk/Asterisk";
import Button from "../../common/Button/Button";
import RadioButton from "../../common/RadioGroup/RadioButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import DatePicker from "../../common/DatePicker/DatePicker";
import { useTranslation } from "react-i18next";
import Dropdown, { Option } from "../../common/Dropdown/Dropdown";
import "./PropertyDetails.css"
import DataTable from "../../common/DataTable/DataTable";
import { exactMatcher, getFormErrorByName, getFormErrorMessageByName, hasError, useFormErrorContext } from "../../../utils/context/FormErrorContext";
import { useReadOnly } from "../../../utils/ReadOnlyContext";
import ErrorMessages from "../../common/ErrorMessages/ErrorMessages";
import { useNavigation } from "../../../utils/context/NavigationContext";
import { toast } from "react-toastify";
import { setMenu } from "../../../redux/actions/menu/menu";
import AGSNavList from "../../../assets/data/navigationList/AGSNavList";
import i18n from "../../../i18n";

export default function PropertyDetails(): JSX.Element {
  const dispatch = useDispatch();
  const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
  const assessmentInfoState: AggregateSiteAssessmentInfoState = useSelector(selectGetAggregateSiteAssessmentInfo);
  const currentSnapshotId = sessionStorage.getItem("currentSnapshotId") as string;
  const [isUpdateReqSent, setIsUpdateReqSent] = useState(false);
  const [assessmentInfo, setAssessmentInfo] = useState<AggregateSiteAssessmentInfo>({ ...defaultAggregateSiteAssessmentInfo });
  const saveData: SaveDataState = useSelector(selectSaveData);
  const saveResultState: SaveResultState = useSelector(selectSaveResult);
  const elsLookupsState: GetElsLookupsState = useSelector(selectELSLookups);
  const { t } = useTranslation("property_details");
  const analysisFlagOptions = (elsLookupsState.dropdowns && elsLookupsState.dropdowns["rentAnalysisOptions"] as Option[]) ?? []
  const aggregateOperationTypes = (elsLookupsState.dropdowns && elsLookupsState.dropdowns["aggregateOperationTypeOptions"] as Option[]) ?? []
  const profileType = profileOverview.profile.profile_type_code;
  const isInternalUser = sessionStorage.getItem("isInternalUser") ? true : false;
  const { isReadOnly } = useReadOnly()
  const { errors, removeError, clearErrors } = useFormErrorContext();
  const { direction } = useNavigation()
  const [dataError, setDataError] = useState(false)
  const menuState = useSelector(selectMenu);

  useEffect(() => {
    if (!elsLookupsState.dropdowns) {
      const propertyCode = sessionStorage.getItem("propertyCode") ?? "";
      dispatch(getElsLookups(propertyCode))
    }
  }, [elsLookupsState.dropdowns, dispatch]);

  useEffect(() => {
    if (currentSnapshotId && profileOverview.profile.profile_type_code && !assessmentInfoState.loading
      && (!assessmentInfoState.success || currentSnapshotId !== assessmentInfoState.snapshot_id)) {
      if (!dataError) {
        dispatch(getAssessmentInfo(currentSnapshotId, profileOverview.profile.profile_type_code))
        if (!assessmentInfoState.success && assessmentInfoState.error) {
          toast.error(assessmentInfoState.message || "Failed to find assessment info with snapshot id");
          setDataError(true)
        }
      }
    }
  }, [assessmentInfoState, currentSnapshotId, dispatch, profileOverview.profile.profile_type_code, dataError]);

  useEffect(() => {
    if (!dataError) {
      let data = { ...assessmentInfo }
      if (Object.keys(assessmentInfoState.formData).length !== 0) {
        data = { ...assessmentInfoState.formData as AggregateSiteAssessmentInfo }
      }
      if (!data["analysisTypes"] || data["analysisTypes"]?.length === 0) {
        data["analysisTypes"] = defaultAggregateSiteAnalysisTypes
      }
      data["analysisTypes"].forEach(e => e.componentId = String(crypto.randomUUID()))
      setAssessmentInfo(data)
    }
    // eslint-disable-next-line
  }, [assessmentInfoState, dataError])

  useEffect(() => {
    if (saveData.saving) {
      clearErrors();
      const triggerSpectral = isInternalUser || direction !== "backward"
      let spectralSection = null
      if (profileType === "AGS") {
        spectralSection = "ags-license-details"
      }
      if (assessmentInfo.licenseOperationTypeCode === "N" || (["P", "Q", "B"].includes(assessmentInfo.licenseOperationTypeCode as string) && assessmentInfo.licenseActiveFlag)) {
        assessmentInfo["surrenderRelinquishLicenseFlag"] = false
      }
      const data = {
        licenseOperationTypeCode: assessmentInfo["licenseOperationTypeCode"],
        licenseActiveFlag: assessmentInfo["licenseActiveFlag"],
        licenses: assessmentInfo["licenses"],
        surrenderRelinquishLicenseFlag: assessmentInfo["surrenderRelinquishLicenseFlag"],
        surrenderRelinquishLicenseDate: assessmentInfo["surrenderRelinquishLicenseDate"],
        rehabilitatedLicensedLandFlag: assessmentInfo["rehabilitatedLicensedLandFlag"],
        unlicensedLandFlag: assessmentInfo["unlicensedLandFlag"],
        licensedPondFlag: assessmentInfo["licensedPondFlag"],
        belowWaterExtractionFlag: assessmentInfo["belowWaterExtractionFlag"],
        attachments: assessmentInfo["attachments"],
        land: assessmentInfo["land"],
        pond: assessmentInfo["pond"],
        analysisTypes: assessmentInfo["analysisTypes"],
        licensedLands1: assessmentInfo["licensedLands1"],
        licensedLands2: assessmentInfo["licensedLands2"],
        licensedLands3: assessmentInfo["licensedLands3"],
        unlicensedLands: assessmentInfo["unlicensedLands"]
      }
      dispatch(putAssessmentInfo(currentSnapshotId, {
        "data": { ...data },
        "spectral_section": spectralSection,
        "trigger_spectral": triggerSpectral,
        "profile_type_code": "AGS",
        "is_internal": isInternalUser
      }));
      setIsUpdateReqSent(true);
    }
  }, [assessmentInfo, currentSnapshotId, dispatch, saveData.saving, profileType, clearErrors, isInternalUser, direction]);

  useEffect(() => {
    if (isUpdateReqSent && !saveResultState.loading) {
      if (saveResultState.status === 200) {
        dispatch(getAssessmentInfo(currentSnapshotId, profileOverview.profile.profile_type_code));
      }
      setIsUpdateReqSent(false);
    }
  }, [currentSnapshotId, dispatch, isUpdateReqSent, profileOverview.profile.profile_type_code, saveResultState]);

  const addLicense = () => {
    const updatedLicenses = assessmentInfo.licenses || []
    updatedLicenses.push({ name: "", number: "", componentId: String(crypto.randomUUID()) })
    setAssessmentInfo({ ...assessmentInfo, licenses: updatedLicenses })
  }

  const tableHeadings = [
    <th className="prop-details-table-heading-1" key="column-1-header">{t("name")} <Asterisk /></th>,
    <th className="prop-details-table-heading-1" key="column-2-header">{t("licenseNumber")} <Asterisk /></th>,
    <th className="prop-details-table-heading-2" key="column-3-header"></th>
  ]

  const buildTableContent = useCallback(() => {
    const tableBody: JSX.Element[] = [];
    const licenses = assessmentInfo.licenses
    if (licenses && licenses.length) {
      for (let i = 0; i < licenses.length; i++)
        tableBody.push(<tr key={i}>
          <td>
            <Input name={"assessmentInfo.licenses." + licenses[i].componentId as string + ".name"} id={"assessmentInfo.licenses." + licenses[i].componentId as string + ".name"} type={"text"}
              isError={hasError(errors, "assessmentInfo.licenses." + licenses[i].componentId as string + ".name", exactMatcher)}
              maxLength={50} size={35}
              value={licenses[i].name}
              ariaLabel={"licenseName"}
              onChange={(e) => {
                const updatedLicenses = assessmentInfo["licenses"]
                if (updatedLicenses) {
                  updatedLicenses[i].name = e.target.value
                  setAssessmentInfo({ ...assessmentInfo, licenses: updatedLicenses })
                }
                removeError("assessmentInfo.licenses." + licenses[i].componentId as string + ".name")
              }}
              errorMessage={getFormErrorMessageByName(errors, "assessmentInfo.licenses." + licenses[i].componentId as string + ".name", exactMatcher)}
            />
          </td>
          <td className="license-number-column">
            <Input name={"assessmentInfo.licenses." + licenses[i].componentId as string + ".number"} id={"assessmentInfo.licenses." + licenses[i].componentId as string + ".number"} type={"text"}
              isError={hasError(errors, "assessmentInfo.licenses." + licenses[i].componentId as string + ".number", exactMatcher)}
              maxLength={25} size={35}
              value={licenses[i].number}
              ariaLabel={"licenseNumber"}
              onChange={(e) => {
                const updatedLicenses = assessmentInfo["licenses"]
                if (updatedLicenses) {
                  updatedLicenses[i].number = e.target.value
                  setAssessmentInfo({ ...assessmentInfo, licenses: updatedLicenses })
                }
                removeError("assessmentInfo.licenses." + licenses[i].componentId as string + ".number")
              }}
              errorMessage={getFormErrorMessageByName(errors, "assessmentInfo.licenses." + licenses[i].componentId as string + ".number", exactMatcher)}
            />
          </td>
          <td className="vertical-align-middle trash-column">
            <FontAwesomeIcon className={`cursor-pointer trash` + (isReadOnly ? ` read-only` : "")} icon={faTrashCan} onClick={() => {
              if (!isReadOnly) {
                const updatedLicenses = assessmentInfo["licenses"]?.filter((license, index) => {
                  return license ? index !== i : null;
                })
                if (updatedLicenses) {
                  setAssessmentInfo({ ...assessmentInfo, licenses: updatedLicenses })
                }
              }
            }} />
          </td>
        </tr>
        )
    }
    return (<>{tableBody}</>)
  }, [errors, assessmentInfo, isReadOnly, removeError])

  const handleOperationTypeChange = (value: string) => {
    const menuListWithoutLicensedLands = menuState.menu.filter(menu => !["licensedLands", "licensedPonds", "unlicensedLands", "documents", "agsSummary"].includes(menu.name))
    if (value === "N" || (["P", "Q", "B"].includes(value) && assessmentInfo.licenseOperationTypeCode === "N")) {
      setAssessmentInfo({ ...assessmentInfo, licenseOperationTypeCode: value, surrenderRelinquishLicenseFlag: null, surrenderRelinquishLicenseDate: null })
      if (value === "N") {
        dispatch(setMenu(menuListWithoutLicensedLands))
      } else {
        dispatch(setMenu(structuredClone(AGSNavList)))
      }
    } else {
      setAssessmentInfo({ ...assessmentInfo, licenseOperationTypeCode: value })
      dispatch(setMenu(structuredClone(AGSNavList)))
    }
  }

  const handleLicenseActiveFlagChange = (value: boolean) => {
    if (value || (!value && assessmentInfo.licenseActiveFlag)) {
      if (value) {
        setAssessmentInfo({ ...assessmentInfo, licenseActiveFlag: value, licenses: [{ name: "", number: "", componentId: String(crypto.randomUUID()) }], surrenderRelinquishLicenseFlag: null, surrenderRelinquishLicenseDate: null })
      }
      if (!value) {
        setAssessmentInfo({ ...assessmentInfo, licenseActiveFlag: value, licenses: [], surrenderRelinquishLicenseFlag: null, surrenderRelinquishLicenseDate: null })
      }
    } else {
      setAssessmentInfo({ ...assessmentInfo, licenseActiveFlag: value, licenses: [] })
    }
    removeError(getFormErrorByName(errors, "licenseActiveFlag")?.primaryKey as string)
    removeError(getFormErrorByName(errors, "surrenderRelinquishLicenseFlag")?.primaryKey as string)
    removeError(getFormErrorByName(errors, "surrenderRelinquishLicenseDate")?.primaryKey as string)
  }

  return (<>
    <ErrorMessages errors={errors} />
    <div className="partial-width ags-property-details">
      <table className="ags-table" role="presentation">
        <tbody>
          <tr>
            <td>{t("pitOrQuarry")}</td>
            <td>
              <div className="radio-group-container">
                {aggregateOperationTypes.map((e) =>
                  <RadioButton
                    key={e.value as string}
                    name="pit-or-quarry"
                    id={`pit-or-quarry` + e.value}
                    text={i18n.language === "fr" ? e.frenchText as string : e.text as string}
                    textPosition="right"
                    value={e.value as string}
                    currentValue={assessmentInfo.licenseOperationTypeCode} onChange={() => {
                      handleOperationTypeChange(e.value as string)
                    }}
                    isError={hasError(errors, "licenseOperationTypeCode")}
                    ariaLabel="pit-or-quarry" />
                )}
              </div>
            </td>
          </tr>
          <tr>
            <td>{t("licensePermit")}</td>
            <td>
              <RadioButton name="license-or-permit" id="license-or-permit-yes" text={t("yes")} textPosition="right" value="true"
                currentValue={assessmentInfo.licenseActiveFlag || ""} onChange={() => {
                  handleLicenseActiveFlagChange(true)
                }} className="inline-block margin-right-1"
                isError={hasError(errors, "licenseActiveFlag")}
                ariaLabel="license-or-permit" />
              <RadioButton name="license-or-permit" id="license-or-permit-no" text={t("no")} textPosition="right" value="false"
                currentValue={assessmentInfo.licenseActiveFlag === false ? "false" : ""} onChange={() => {
                  handleLicenseActiveFlagChange(false)
                }} className="inline-block"
                isError={hasError(errors, "licenseActiveFlag")}
                ariaLabel="license-or-permit" />
            </td>
          </tr>
          {assessmentInfo.licenseActiveFlag ? <>
            {!isReadOnly ? <tr>
              <td colSpan={2}>
                <div className='suite-breakdown-btn-container inline-block add-button'>
                  <Button onClick={() => addLicense()} text={t("add") as string} ariaLabel="add-license" />
                </div>
              </td>
            </tr> : <></>}
            <tr>
              <td colSpan={2}>
                <DataTable
                  headings={tableHeadings}
                  className={"assisted-suite-breakdown-table"}>
                  {buildTableContent()}
                </DataTable>
              </td>
            </tr>
          </> : <></>}
          {(["P", "Q", "B"].includes(assessmentInfo.licenseOperationTypeCode as string) && !assessmentInfo.licenseActiveFlag) ?
            <tr>
              <td>{t("isLicenseSurrendered")}</td>
              <td>
                <RadioButton name="license-surrendered" id="license-surrendered-yes" text={t("yes")} textPosition="right" value="true"
                  currentValue={assessmentInfo.surrenderRelinquishLicenseFlag || ""} onChange={() => {
                    setAssessmentInfo({ ...assessmentInfo, surrenderRelinquishLicenseFlag: true })
                    removeError(getFormErrorByName(errors, "surrenderRelinquishLicenseFlag")?.primaryKey as string)
                  }} className="inline-block margin-right-1"
                  isError={hasError(errors, "surrenderRelinquishLicenseFlag")}
                  ariaLabel="license-surrendered" />
                <RadioButton name="license-surrendered" id="license-surrendered-no" text={t("no")} textPosition="right" value="false"
                  currentValue={assessmentInfo.surrenderRelinquishLicenseFlag === false ? "false" : ""} onChange={() => {
                    setAssessmentInfo({ ...assessmentInfo, surrenderRelinquishLicenseFlag: false, surrenderRelinquishLicenseDate: null })
                    removeError(getFormErrorByName(errors, "surrenderRelinquishLicenseFlag")?.primaryKey as string)
                  }} className="inline-block"
                  isError={hasError(errors, "surrenderRelinquishLicenseFlag")}
                  ariaLabel="license-surrendered" />
              </td>
            </tr> : <></>
          }
          {assessmentInfo.surrenderRelinquishLicenseFlag ?
            <tr>
              <td>{t("effectiveDateOfSurrender")}</td>
              <td id="date-of-surrender-field">
                <label className="invisible-legend"><span>{t("effectiveDateOfSurrender")}</span></label>
                <DatePicker id="surrenderRelinquishLicenseDate" name="surrenderRelinquishLicenseDate"
                  date={assessmentInfo.surrenderRelinquishLicenseDate}
                  position="right-end"
                  onChange={(e) => { setAssessmentInfo({ ...assessmentInfo, surrenderRelinquishLicenseDate: e ? new Date(e).toISOString().split('T')[0] : null }) }}
                  ariaLabelledBy="date-of-surrender-field"
                  errorMessage={getFormErrorMessageByName(errors, "surrenderRelinquishLicenseDate")}
                  hasError={hasError(errors, "surrenderRelinquishLicenseDate")} />
              </td>
            </tr> : <></>
          }
        </tbody>
      </table>
    </div>
    {profileType === "AGS" && isInternalUser && sessionStorage.getItem("campaignStatusCode") === "INTERNAL_IN_PROGRESS" && analysisFlagOptions.length > 0 &&
      <div className='airp-badge badge-lighter-suburb-yellow analysis-container margin-top-1'>
        <label htmlFor='analysisFlag'>{t("analysis")}</label>
        <Dropdown id="analysisFlag" name='analysisFlag' className='admin-input-style'
          options={analysisFlagOptions}
          value={assessmentInfo.analysisTypes?.find(e => e.analysisTypeCode === "D")?.analysisFlag}
          onChange={(e) => {
            const temp = assessmentInfo.analysisTypes
            if (temp?.length) {
              const idx = temp?.findIndex(e => e.analysisTypeCode === "D")
              temp[idx] = {
                ...temp[idx],
                "analysisFlag": (e.target as HTMLSelectElement).value,
                "analysisReviewedBy": localStorage.getItem("username") ?? "SYSTEM",
                "analysisReviewedDate": (new Date()).toISOString()
              }
            }
            setAssessmentInfo({ ...assessmentInfo, analysisTypes: temp })
            removeError(getFormErrorByName(errors, assessmentInfo.analysisTypes?.find(e => e.analysisTypeCode === "D")?.componentId as string)?.primaryKey as string)
          }}
          hideErrorCross
          errorMessage={getFormErrorMessageByName(errors, assessmentInfo.analysisTypes?.find(e => e.analysisTypeCode === "D")?.componentId as string, exactMatcher)}
        />
      </div>}
  </>
  )
}